/**
 * Board geometry, and the bitmask a set of cells is carried around as.
 *
 * Regions here are orthogonally connected — diagonally touching cells of the
 * same shade are two regions, not one — so the only neighbourhood the whole game
 * needs is the four edge-sharing cells. Every rule is stated over it: a region is
 * a connected block, a region's border is the ring of cells around it, and two
 * same-shade regions may not share an edge.
 *
 * The solver spends its time asking whether two sets of cells overlap, which on
 * a board of at most 64 cells is two `&` instructions. `Mask` is that: a set of
 * cells as two 32-bit words. It is why the candidate-region search can afford to
 * hold thousands of shapes in memory and test them all.
 */

import { MAX_CELLS } from './types';

// ── Cell sets ───────────────────────────────────────────────────────────────

export interface Mask {
  /** Cells 0–31. */
  lo: number;
  /** Cells 32–63. */
  hi: number;
}

export const EMPTY_MASK: Mask = { lo: 0, hi: 0 };

export function maskOf(cells: Iterable<number>): Mask {
  let lo = 0;
  let hi = 0;
  for (const cell of cells) {
    if (cell < 32) lo |= 1 << cell;
    else hi |= 1 << (cell - 32);
  }
  return { lo, hi };
}

export function maskWith(mask: Mask, cell: number): Mask {
  return cell < 32
    ? { lo: mask.lo | (1 << cell), hi: mask.hi }
    : { lo: mask.lo, hi: mask.hi | (1 << (cell - 32)) };
}

export function maskHas(mask: Mask, cell: number): boolean {
  return cell < 32 ? (mask.lo & (1 << cell)) !== 0 : (mask.hi & (1 << (cell - 32))) !== 0;
}

export function maskUnion(a: Mask, b: Mask): Mask {
  return { lo: a.lo | b.lo, hi: a.hi | b.hi };
}

export function maskIntersect(a: Mask, b: Mask): Mask {
  return { lo: a.lo & b.lo, hi: a.hi & b.hi };
}

export function maskWithout(a: Mask, b: Mask): Mask {
  return { lo: a.lo & ~b.lo, hi: a.hi & ~b.hi };
}

export function maskOverlaps(a: Mask, b: Mask): boolean {
  return (a.lo & b.lo) !== 0 || (a.hi & b.hi) !== 0;
}

export function maskEmpty(mask: Mask): boolean {
  return mask.lo === 0 && mask.hi === 0;
}

export function maskEquals(a: Mask, b: Mask): boolean {
  return a.lo === b.lo && a.hi === b.hi;
}

export function maskSize(mask: Mask): number {
  return popcount(mask.lo) + popcount(mask.hi);
}

function popcount(word: number): number {
  let value = word - ((word >> 1) & 0x55555555);
  value = (value & 0x33333333) + ((value >> 2) & 0x33333333);
  value = (value + (value >> 4)) & 0x0f0f0f0f;
  return (value * 0x01010101) >> 24;
}

export function maskCells(mask: Mask): number[] {
  // Set bits only, lowest first: the solver calls this on every block it
  // settles, and a block is a handful of cells in a word of thirty-two.
  const out: number[] = [];
  for (let word = mask.lo; word !== 0; word &= word - 1) out.push(31 - Math.clz32(word & -word));
  for (let word = mask.hi; word !== 0; word &= word - 1) {
    out.push(63 - Math.clz32(word & -word));
  }
  return out;
}

/** Identity of a cell set, for de-duplicating shapes while enumerating them. */
export function maskKey(mask: Mask): string {
  return `${mask.lo >>> 0}:${mask.hi >>> 0}`;
}

// ── Geometry ────────────────────────────────────────────────────────────────

export interface Geometry {
  width: number;
  height: number;
  cells: number;
  /** The four edge-sharing cells of each cell, clipped at the border. */
  neighbors: number[][];
  /** The same, as a mask, for building a region's border in one pass. */
  neighborMask: Mask[];
  /** Every cell on the board. */
  full: Mask;
}

const cache = new Map<string, Geometry>();

/**
 * The geometry for one board size, built once and shared.
 *
 * Four board sizes exist and every puzzle of a size wants the same tables, so
 * they are computed on first use and kept. Nothing here depends on a puzzle —
 * only on the grid.
 */
export function geometry(width: number, height: number): Geometry {
  const key = `${width}x${height}`;
  const known = cache.get(key);
  if (known) return known;

  const cells = width * height;
  if (cells > MAX_CELLS) {
    throw new Error(`board ${key} has ${cells} cells; masks hold ${MAX_CELLS}`);
  }

  const neighbors: number[][] = [];
  for (let cell = 0; cell < cells; cell += 1) {
    const row = Math.floor(cell / width);
    const column = cell % width;
    const list: number[] = [];
    if (row > 0) list.push(cell - width);
    if (row < height - 1) list.push(cell + width);
    if (column > 0) list.push(cell - 1);
    if (column < width - 1) list.push(cell + 1);
    neighbors.push(list);
  }

  const built: Geometry = {
    width,
    height,
    cells,
    neighbors,
    neighborMask: neighbors.map((list) => maskOf(list)),
    full: maskOf(Array.from({ length: cells }, (_, cell) => cell)),
  };
  cache.set(key, built);
  return built;
}

/**
 * The cell a half turn of the board puts this one on.
 *
 * Row-major order makes it one subtraction: turning the grid end over end
 * reverses the order the cells are read in. No board has a cell that lands on
 * itself — a fixed point needs an odd width *and* an odd height, and every board
 * in `BOARDS` has an even side — which is what lets the generator pair every
 * cell with a different one.
 */
export function turned(geo: Geometry, cell: number): number {
  return geo.cells - 1 - cell;
}

/** The cells around a set but not in it — a region's border. */
export function borderOf(geo: Geometry, cells: Mask): Mask {
  let ring = EMPTY_MASK;
  for (const cell of maskCells(cells)) {
    ring = maskUnion(ring, geo.neighborMask[cell] as Mask);
  }
  return maskWithout(ring, cells);
}

// ── Regions ─────────────────────────────────────────────────────────────────

export interface Region {
  /** The shade every cell in it carries. Never UNKNOWN. */
  shade: number;
  cells: number[];
  mask: Mask;
}

/**
 * The connected blocks of one shade, given a partial or complete shading.
 *
 * Cells the player has not decided are not part of any region — an undecided
 * cell is not yet touching anything. That is what lets the same function serve
 * the generator, which passes a finished grid, and the board on screen, which is
 * mostly holes.
 */
export function regionsOf(geo: Geometry, shades: readonly number[]): Region[] {
  const seen = new Uint8Array(geo.cells);
  const out: Region[] = [];
  for (let start = 0; start < geo.cells; start += 1) {
    const shade = shades[start] as number;
    if (shade === 0 || seen[start] === 1) continue;
    const cells: number[] = [start];
    seen[start] = 1;
    for (let read = 0; read < cells.length; read += 1) {
      const cell = cells[read] as number;
      for (const next of geo.neighbors[cell] as number[]) {
        if (seen[next] === 1 || shades[next] !== shade) continue;
        seen[next] = 1;
        cells.push(next);
      }
    }
    out.push({ shade, cells, mask: maskOf(cells) });
  }
  return out;
}
